'use client';

import { Logout, Add, List } from '@mui/icons-material';
import { Tooltip, Avatar, Box, Menu, MenuItem, Divider, ListItemIcon, ButtonBase, useMediaQuery } from '@mui/material';
import { User } from '@prisma/client';
import Image from 'next/image';
import { useState } from 'react';
import { signOut, useSession } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import classnames from 'classnames';

import { LoginComponent } from '../Login';
import LoadingComponent from '../Loading';

import styles from './styles.module.scss';

interface UserProps {
  className?: string;
}

export function UserComponent({ className = '' }: UserProps) {
  const { data, status } = useSession();
  const user: User = data?.user;

  const { push } = useRouter();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const isSmallMobile = useMediaQuery('(max-width:768px)');

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!isSmallMobile) {
      setAnchorEl(event.currentTarget);
    }
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div
      className={classnames(styles.userContainer, {
        [className]: className
      })}
    >
      {status === 'loading' ? (
        <div className={styles.userContent}>
          <LoadingComponent mini />
        </div>
      ) : (
        <>
          {status !== 'authenticated' ? (
            <LoginComponent />
          ) : (
            <div className={styles.userContent}>
              <Box sx={{ display: 'flex', alignItems: 'center', textAlign: 'center' }}>
                <Tooltip title="Configurações da conta">
                  <ButtonBase
                    onClick={handleClick}
                    aria-controls={open ? 'account-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                    sx={{
                      gap: 1,
                      color: 'white',
                      borderRadius: '8px',
                      padding: '0px 0px',

                      '& .MuiAvatar-root': {
                        width: 24,
                        height: 24
                      }
                    }}
                  >
                    <p className={styles.userName}>Olá, {user?.firstName}</p>
                    {user?.image ? (
                      <Image
                        unoptimized
                        src={user?.image}
                        width={24}
                        height={24}
                        alt="user"
                        className={styles.userAvatar}
                        quality={100}
                      />
                    ) : (
                      <Avatar sx={{ width: 24, height: 24 }}>{user?.firstName?.charAt(0)}</Avatar>
                    )}
                  </ButtonBase>
                </Tooltip>
              </Box>
              <Menu
                anchorEl={anchorEl}
                id="account-menu"
                open={open}
                onClose={handleClose}
                onClick={handleClose}
                PaperProps={{
                  elevation: 0,
                  sx: {
                    overflow: 'visible',
                    filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                    mt: 1.5,

                    '& .MuiAvatar-root': {
                      width: 32,
                      height: 32
                    },

                    '&:before': {
                      content: '""',
                      display: 'block',
                      position: 'absolute',
                      top: 0,
                      right: 28,
                      width: 10,
                      height: 10,
                      bgcolor: 'background.paper',
                      transform: 'translateY(-50%) rotate(45deg)',
                      zIndex: 0
                    }
                  }
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                <MenuItem
                  onClick={handleClose}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,

                    '& .MuiAvatar-root': {
                      width: 32,
                      height: 32
                    },
                    '& .userAvatar': {
                      borderRadius: '50%'
                    }
                  }}
                >
                  {user?.image ? (
                    <Image
                      unoptimized
                      src={user?.image}
                      width={32}
                      height={32}
                      alt="user"
                      className={styles.userAvatar}
                      style={{ borderRadius: '50%' }}
                    />
                  ) : (
                    <Avatar sx={{ width: 32, height: 32 }}>{user?.firstName?.charAt(0)}</Avatar>
                  )}
                  <p className={styles.userName}>Perfil</p>
                </MenuItem>

                <Divider />
                <MenuItem
                  onClick={() => {
                    push('/report');
                  }}
                >
                  <ListItemIcon>
                    <Add fontSize="small" />
                  </ListItemIcon>
                  Criar um reporte
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    push('/user/my-reports');
                  }}
                >
                  <ListItemIcon>
                    <List fontSize="small" />
                  </ListItemIcon>
                  Meus reportes
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    signOut();
                  }}
                >
                  <ListItemIcon>
                    <Logout fontSize="small" />
                  </ListItemIcon>
                  Sair
                </MenuItem>
              </Menu>
            </div>
          )}
        </>
      )}
    </div>
  );
}
